import { useState, useEffect, useRef, useCallback } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { logout } from '../Redux/Auth/AuthActions';
import { get } from '../Services/ApiService';
import { setAuthToken } from '../Services/InterceptorService';
import { ServerEndpoint } from '../Redux/endpoints.ts';

const WARN_SECS  = 30 * 60; // badge appears
const MODAL_SECS = 15 * 60; // modal appears

function getLocalExp() {
    try {
        const token = localStorage.getItem('token');
        if (!token) return null;
        return JSON.parse(atob(token.split('.')[1])).exp;
    } catch {
        return null;
    }
}

export function useSessionExpiry() {
    const dispatch = useDispatch();
    const isAuth   = useSelector(s => s.auth.isAuthenticated);

    const [secsLeft, setSecsLeft] = useState(null);
    const [showModal, setShowModal] = useState(false);

    const expiresAtRef = useRef(null);
    const pollRef      = useRef(null);
    const tickRef      = useRef(null);

    const tick = useCallback(() => {
        if (!expiresAtRef.current) return;
        const secs = expiresAtRef.current - Math.floor(Date.now() / 1000);
        if (secs <= 0) {
            dispatch(logout());
            return;
        }
        setSecsLeft(secs <= WARN_SECS ? secs : null);
        setShowModal(secs <= MODAL_SECS);
    }, [dispatch]);

    const refresh = useCallback(async () => {
        try {
            const res  = await get(ServerEndpoint.LOGIN_REFRESH);
            const data = res?.data?.data;
            if (data?.token) {
                localStorage.setItem('token', data.token);
                setAuthToken(data.token);
                expiresAtRef.current = data.expiresAt || getLocalExp();
                setShowModal(false);
                tick();
            }
        } catch {
            // refresh failed — let session expire naturally
        }
    }, [tick]);

    useEffect(() => {
        if (!isAuth) {
            clearInterval(pollRef.current);
            clearInterval(tickRef.current);
            setSecsLeft(null);
            setShowModal(false);
            expiresAtRef.current = null;
            return;
        }

        expiresAtRef.current = getLocalExp();
        tick();
        tickRef.current = setInterval(tick, 1000);

        const serverCheck = async () => {
            try {
                const res = await get(ServerEndpoint.LOGIN_CHECK);
                const exp = res?.data?.data?.expiresAt;
                if (exp) expiresAtRef.current = exp;
                tick();
            } catch {
                // 401 handled by InterceptorService
            }
        };
        pollRef.current = setInterval(serverCheck, 60 * 1000);

        return () => {
            clearInterval(pollRef.current);
            clearInterval(tickRef.current);
        };
    }, [isAuth, tick]);

    return { secsLeft, showModal, refresh };
}
